import * as React from 'react';

import { IBuyboxCallbacks, IBuyboxData, IBuyboxState, IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox';
import { Heading, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { IAddToCartFailureResult, PriceComponent } from '@msdyn365-commerce/components';
import { getUrlSync, IImageSettings } from '@msdyn365-commerce/core';
import { DeliveryOption, ProductDimension } from '@msdyn365-commerce/retail-proxy';
import { IBuyboxProps, IBuyboxResources as IBuyboxViewResources } from '../../definition-extensions/buybox.ext.props.autogenerated';
import { IBuyboxData as IBuyboxViewData } from '../buybox.data';
import { BuyboxErrorBlock } from './get-add-to-cart';
import { getConfigureErrors, getGenericError } from './utilities/error-utilities';
import Popup, { IDobbiesPopUpProps } from './pdp-overlay-popup';

export interface IFindInStoreFailureResult {
    missingDimensions?: ProductDimension[];
}

export interface IBuyboxFindInStoreViewProps {
    storeSelector?: React.ReactNode;
    storeSelectors?: React.ReactNode;

    heading?: React.ReactNode;
    description?: React.ReactNode;
    errors?: React.ReactNode;
    clickAndCollect?: React.ReactNode;
    checkStock?: React.ReactNode;
    productPickupOptionList?: React.ReactNode;

    modal?: React.ReactNode;

    ContainerProps: INodeProps;
    /* VSI Customization - START - 12/02/21 - To display error if any */
    isError?: boolean;
    isAddedToCart?: boolean;
    /* VSI Customization - END */
    openFindInStoreDialog(): Promise<void>;
}

// Custom Implementation of Buybox props

// tslint:disable-next-line: max-func-body-length
export function getBuyboxFindInStore(
    props: IBuyboxViewProps & IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    resources: IBuyboxViewResources,
    onAddToCartClicked?: () => void,
    setAddToCartModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
): IBuyboxFindInStoreViewProps | undefined {
    const {
        data,
        slots: { storeSelectors },
        resources: {
            priceFree,
            originalPriceText,
            currentPriceText,
            findInStoreHeaderText,
            findInStoreDescriptionText,
            findInStoreLinkText,
            buyBoxGoToCartText,
            buyBoxContinueShoppingText,
            buyBoxSingleItemText,
            buyBoxMultiLineItemFormatText,
            buyBoxHeaderMessageText,
            descriptionTextToShowAllPickupOptions
        }
    } = props;

    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const [isError, SetIsError] = React.useState(false);
    const [isAddedToCart, SetIsAddedToCart] = React.useState(false);
    const [localSelected, setLocalSelected] = React.useState(1);
    const [hasCnC, setHasCnC] = React.useState(false);
    const [isHover, setIsHover] = React.useState(false);
    const product = data?.product?.result;
    const price = data?.productPrice?.result;
    const storeSelectorStateManager = data?.storeSelectorStateManager?.result;
    const deliveryOptions = data?.deliveryOptions?.result;
    const retailMulitplePickupMFeatureState = data?.featureState?.result?.find(
        featureState => featureState.Name === multiplePickupStoreSwitchName
    )?.IsEnabled;

    const {
        errorState: { configureErrors, quantityError, otherError, errorHost },
        modalOpen,
        quantity
    } = state;

    const optionCnC = deliveryOptions?.DeliveryOptions?.find(option => option.Code === 'ClickCol');
    React.useEffect(() => {
        if (storeSelectorStateManager && storeSelectors && storeSelectors.length !== 0 && optionCnC) {
            const allAttributes = props.data.productAttributes.result;

            const isvendorShippedAttribute =
                allAttributes &&
                allAttributes.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });
            const isVendorShipTextValue =
                isvendorShippedAttribute && isvendorShippedAttribute.TextValue && isvendorShippedAttribute.TextValue.trim().toLowerCase();
            const isVendorShiped = isVendorShipTextValue && (isVendorShipTextValue === 'yes' || isVendorShipTextValue === 'true');
            !!isVendorShiped === false && setHasCnC(true);
        }
    }, [product]);

    const onClick = (val: number) =>
        findInStoreOnClick(
            props,
            state,
            callbacks,
            resources,
            SetIsAddedToCart,
            val,
            setLocalSelected,
            onAddToCartClicked,
            setAddToCartModalOpen
        );
    // if (!product || !storeSelectorStateManager || storeSelectors.length === 0) {
    //     return undefined;
    // }
    // // If no delivery options present on the product, or none of the delivery options
    // // match the PickupDeliveryModeCode, that means the item cannot be used for BOPIS
    // if (!deliveryOptions || !deliveryOptions.DeliveryOptions || !PickupDeliveryModeCode || !deliveryOptions.DeliveryOptions.find(option => option.Code === PickupDeliveryModeCode)) {
    //     return undefined;
    // }
    const dialogStrings = {
        goToCartText: buyBoxGoToCartText,
        continueShoppingText: buyBoxContinueShoppingText,
        headerItemOneText: buyBoxSingleItemText,
        headerItemFormatText: buyBoxMultiLineItemFormatText,
        headerMessageText: buyBoxHeaderMessageText,
        freePriceText: priceFree,
        originalPriceText: originalPriceText,
        currentPriceText: currentPriceText
    };
    const priceComponent = price ? (
        <PriceComponent
            data={{ price: price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={dialogStrings.freePriceText}
            originalPriceText={dialogStrings.originalPriceText}
            currentPriceText={dialogStrings.currentPriceText}
        />
    ) : (
        ''
    );
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: `w=240&h=240&m=6`, w: 0, h: 0 },
            lg: { q: `w=240&h=240&m=6`, w: 0, h: 0 },
            xl: { q: `w=240&h=240&m=6`, w: 0, h: 0 }
        },
        lazyload: true
    };
    const popupProps: IDobbiesPopUpProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: { product: props.data.product?.result, price: props.data.productPrice?.result },
        dialogStrings: dialogStrings,
        imageSettings: defaultImageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: quantity,
        priceComponent: priceComponent,
        navigationUrl: getUrlSync('cart', props.context.actionContext),
        modalOpen: modalOpen,
        setModalOpen: callbacks.changeModalOpen,
        customerInformation: props.data.customerInformation.result,
        unbxdCarousel: props.slots.unbxdCarousel
    };
    const renderModalPopup = <Popup {...popupProps} />;

    return {
        ContainerProps: {
            className: 'ms-buybox__find-in-store'
        },
        storeSelector: storeSelectors[localSelected],
        openFindInStoreDialog: () => onClick(0),
        heading: <Heading className='ms-buybox__find-in-store-heading' headingTag='h4' text={findInStoreHeaderText} />,
        description: <p className='ms-buybox__find-in-store-description'>{findInStoreDescriptionText}</p>,
        productPickupOptionList: retailMulitplePickupMFeatureState ? (
            <React.Fragment>
                <p className='ms-buybox__find-in-store-description'>{descriptionTextToShowAllPickupOptions}</p>
                <ul role='menu' className={`ms-buybox__find-in-store-pickup`}>
                    {deliveryOptions?.DeliveryOptions?.map((item: DeliveryOption) => {
                        return (
                            <li role='presentation' className={`ms-buybox__find-in-store-pickup-option`} key={item.Code}>
                                {item.Description}
                            </li>
                        );
                    })}
                </ul>
            </React.Fragment>
        ) : null,
        errors: (
            <BuyboxErrorBlock
                configureErrors={configureErrors}
                quantityError={quantityError}
                otherError={otherError}
                resources={props.resources}
                showError={errorHost === 'FINDINSTORE'}
                SetIsError={SetIsError}
            />
        ),
        checkStock: (
            <button
                className='ms-buybox__find-in-store-local-button '
                onClick={() => onClick(1)}
                aria-label='Check stock in your local store'
            >
                <div className='row ms-buybox__find-in-store-local-container'>
                    <div className='msc-in-stock' />
                    {/* isCnC is not used for now, the stores are displayed in general */}

                    {true ? 'Check stock in your local store' : 'Unavailable in your local store'}
                </div>
            </button>
        ),
        clickAndCollect: optionCnC && (
            <button className='msc-add-to-cart' disabled={!hasCnC} aria-label={findInStoreLinkText} onClick={() => onClick(0)}>
                <div
                    className='row ms-buybox__find-in-store-button'
                    color='secondary'
                    onMouseLeave={e => {
                        setIsHover(false);
                    }}
                    onMouseEnter={e => {
                        if (hasCnC) {
                            setIsHover(true);
                        }
                    }}
                >
                    <div className={isHover ? 'msc-delivery__isCnC-active' : 'msc-delivery__isCnC'} />

                    <div className='msc-delivery__isCnC-text'>{findInStoreLinkText}</div>
                </div>
            </button>
        ),
        isError: isError,
        isAddedToCart: isAddedToCart,
        modal: renderModalPopup
    };
}

// tslint:disable-next-line: max-func-body-length
async function findInStoreOnClick(
    props: IBuyboxViewProps & IBuyboxProps<IBuyboxData & IBuyboxViewData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    resources: IBuyboxViewResources,
    SetIsAddedToCart: React.Dispatch<React.SetStateAction<boolean>>,
    val: number,
    setLocalSelected: React.Dispatch<React.SetStateAction<number>>,
    onAddToCartClicked?: () => void,
    setAddToCartModalOpen?: React.Dispatch<React.SetStateAction<boolean>>
): Promise<void> {
    const {
        data: {
            storeSelectorStateManager: { result: storeSelectorStateManager },
            cart: { result: cart },
            productAvailableQuantity: { result: productAvailableQuantity }
        },
        context,
        modules
    } = props;

    const { selectedProduct, quantity } = state;

    setLocalSelected(val);

    let dataProduct = props.data.product.result;
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    const retailMulitplePickupMFeatureState = props.data?.featureState?.result?.find(
        featureState => featureState.Name === multiplePickupStoreSwitchName
    )?.IsEnabled;

    if (selectedProduct) {
        dataProduct = (await selectedProduct) || dataProduct;
    }

    if (!dataProduct || !storeSelectorStateManager) {
        return;
    }

    const product = dataProduct;

    const missingDimensions =
        product.Dimensions && product.Dimensions.filter(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value));
    let storeSelectorId: string = '';
    if (modules && Object.keys(modules).length > 0 && modules.storeSelectors && modules.storeSelectors.length > 0) {
        storeSelectorId = modules.storeSelectors[val].id;
    }

    if (missingDimensions && missingDimensions.length > 0) {
        if (callbacks.updateErrorState) {
            callbacks.updateErrorState({
                errorHost: 'FINDINSTORE',
                configureErrors: getConfigureErrors(missingDimensions, resources)
            });
        }
    } else {
        storeSelectorStateManager
            .openDialog({
                product,
                id: storeSelectorId,
                deliveryOptions: retailMulitplePickupMFeatureState ? props.data.deliveryOptions.result : undefined,
                onLocationSelected: (orgUnitLocation, deliveryMode) => {
                    if (!cart) {
                        return Promise.resolve();
                    }

                    const {
                        context: {
                            app: { config }
                        }
                    } = props;
                    const behavior = config && config.addToCartBehavior;

                    return cart
                        .addProductToCart({ product: product, count: quantity, location: orgUnitLocation, deliveryMode: deliveryMode })
                        .then(result => {
                            if (result.status === 'FAILED' && result.substatus === 'MAXQUANTITY') {
                                // callbacks.changeModalOpen(false);
                                setAddToCartModalOpen && setAddToCartModalOpen(false);
                                const productAvailability = state.productAvailableQuantity
                                    ? state.productAvailableQuantity.ProductAvailableQuantity
                                    : productAvailableQuantity && productAvailableQuantity.length
                                    ? productAvailableQuantity[0].ProductAvailableQuantity
                                    : undefined;

                                const failureResult: IAddToCartFailureResult = {
                                    failureReason: 'CARTACTIONFAILED',
                                    cartActionResult: { status: result.status, substatus: result.substatus }
                                };
                                callbacks.updateErrorState({
                                    errorHost: 'ADDTOCART',
                                    configureErrors: {},
                                    otherError: getGenericError(
                                        failureResult,
                                        cart,
                                        resources,
                                        context,
                                        product,
                                        productAvailability,
                                        orgUnitLocation
                                    )
                                });
                            } else {
                                if (behavior === undefined || behavior === 'goToCart' || behavior === 'nothing') {
                                    if (result.status === 'SUCCESS') {
                                        SetIsAddedToCart(true);
                                        if (onAddToCartClicked) {
                                            onAddToCartClicked();
                                        }
                                    }
                                } else if (behavior === 'showModal') {
                                    callbacks.updateErrorState({
                                        configureErrors: {}
                                    });
                                    setAddToCartModalOpen && setAddToCartModalOpen(true);
                                    // callbacks.changeModalOpen(true);
                                }
                            }
                        })
                        .catch((error: Error) => {
                            if (props.telemetry) {
                                props.telemetry.exception(error);
                                props.telemetry.debug('Unable to add product to cart');
                            }
                            return;
                        });
                }
            })
            .catch((error: Error) => {
                if (props.telemetry) {
                    props.telemetry.error(error.message);
                    props.telemetry.debug('Unable to find in store');
                }
                return;
            });
    }

    return;
}
